import { Home } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';

import { ClickHandler } from '../../../../utils/clickEvent';
import { CustomPinIcon } from './GoogleMapPin';
import { NullableStop } from './stopTypes';

// Neutral color for the "Clinician home" legend key. On clinician-color-coded
// maps the home pins are per-clinician colored, so the legend uses one
// representative neutral house rather than a color swatch.
export const CLINICIAN_HOME_LEGEND_COLOR = '#555';

// Up-to-two-letter initials from a full name, for labelling the home marker so a
// clinician's home is identifiable without relying on color.
const initialsFromName = (name?: string | null): string | undefined => {
    if (!name) {
        return undefined;
    }
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return undefined;
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase() || undefined;
};

// Shared clinician-home map marker (PRD-669). A house icon labelled with the
// clinician's initials so it reads as "a home" and says whose, independent of
// color (color-blind safety + maps where the home color isn't per-clinician).
export const clinicianHomePinIcon = (initials?: string): CustomPinIcon => {
    const component = ({ onClick, color }: { onClick?: ClickHandler; color?: string }) => (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                // PERFECTLY place the icon tip on the coordinate.
                transform: 'translate(-50%, -75%)',
            }}
        >
            <IconButton
                onClick={onClick}
                aria-label={initials ? `${initials} home location` : 'Clinician home location'}
                sx={{ p: 0 }}
            >
                <Home sx={{ fontSize: 32, color: color ?? CLINICIAN_HOME_LEGEND_COLOR }} />
            </IconButton>
            {initials ? (
                // Overlay covers the icon and centers the initials in the solid house
                // body *above* the door cut-out — the door notch is at the bottom-center,
                // so the label is nudged up (pb) to keep it from being clipped there.
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pb: '4px',
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        component='span'
                        sx={{
                            fontSize: 8,
                            fontWeight: 700,
                            lineHeight: 1,
                            color: 'white',
                        }}
                    >
                        {initials}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
    component.displayName = 'ClinicianHomePinIcon';
    return component;
};

// Builds a standalone home stop for a clinician (not part of a route). Returns
// null when the clinician has no home coordinates so callers can filter without
// extra guards.
//
// `colorByClinician` (default true): key the home by clinician id so its color
// comes from the same `group_id → getGroupColor` path as that clinician's visit
// pins (matches their color on clinician-color-coded maps). Pass false on maps
// colored by something else (e.g. Live View's attention state) to render a
// neutral house — identity then comes from the initials, not color.
//
// `clinicianName` labels the house with initials and names whose home it is.
export const buildClinicianHomeStop = ({
    clinicianId,
    clinicianName,
    lat,
    lng,
    colorByClinician = true,
}: {
    clinicianId: string;
    clinicianName?: string | null;
    lat: string | null | undefined;
    lng: string | null | undefined;
    colorByClinician?: boolean;
}): NullableStop | null => {
    if (!lat || !lng) {
        return null;
    }
    return {
        lat,
        lng,
        group_id: colorByClinician ? clinicianId : null,
        datetime: null,
        customPinIcon: clinicianHomePinIcon(initialsFromName(clinicianName)),
        popoverComponent: (
            <Typography variant='body2'>
                {clinicianName ? `${clinicianName}'s home` : 'Clinician home'}
            </Typography>
        ),
    };
};
